import { useRef } from 'react';
import classNames from 'classnames';
import { Cell } from '../cells';
import { ResolvedAnchorValue } from 'domains/designers/RuleDesigner/components/ResolvedAnchorValue';
import { PhraseItem } from '../components';
import { ValidationIcon } from 'domains/designers/common';
import { useDidUpdateEffect } from 'hooks/useDidUpdateEffect';
import { PhraseColumn } from '../columns';
import { IHeaderAction, HeaderActions } from 'domains/designers/TruthTable/components/HeaderActions';
import './HeaderPhraseCell.less';
import { getColumnSettings } from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/TruthTable/TruthTableEditService';
import { conditionalArray } from 'utils/ArrayUtils';
import { IHeaderSubAction } from '../components/HeaderAction';
import { RuleTableHeaderDisplayData } from '@decisions/server/DecisionsFramework/Design/Flow/RuleTable/RuleTableHeaderDisplayData';
import { getPhraseColumnName, NoAnchorText, NoVerbText } from '../TruthTableUtils';

export interface PhraseItemClickEvent<T> {
  target: HTMLButtonElement;
  header: T;
}

export type PhraseItemClickCallback = (event: PhraseItemClickEvent<RuleTableHeaderDisplayData>) => void;
export type HeaderActionCallback = (event: { header: RuleTableHeaderDisplayData }) => void;

export interface IHeaderPhraseCellProps {
  className?: string;
  sessionId: string;
  header: RuleTableHeaderDisplayData;
  canDelete: boolean;
  isDebugMode: boolean;
  onAnchorClick?: PhraseItemClickCallback;
  onVerbClick?: PhraseItemClickCallback;
  onMoveColumnToLeft: HeaderActionCallback;
  onMoveColumnToRight: HeaderActionCallback;
  onDeleteClick?: HeaderActionCallback;
  onAddBefore: HeaderActionCallback;
  onAddAfter: HeaderActionCallback;
}

const getClassName = (className?: string) => classNames('tt-header-phrase', className);

/**
 * Header phrase cell displays "Anchor" and "Verb" with ability to edit them.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function HeaderPhraseCell({
  className,
  canDelete,
  header,
  isDebugMode,
  sessionId,
  onAnchorClick,
  onVerbClick,
  onDeleteClick,
  onAddBefore,
  onAddAfter,
  onMoveColumnToLeft,
  onMoveColumnToRight,
}: IHeaderPhraseCellProps) {
  const anchorText = header.AnchorInfo || NoAnchorText;
  const verbText = header.VerbInfo || NoVerbText;
  const headerIssues = header.ValidationIssues;
  const anchorRef = useRef<HTMLButtonElement>(null);
  const verbRef = useRef<HTMLButtonElement>(null);
  const actions: IHeaderAction[] = [];

  actions.push({
    iconClassName: 'tt-result-edit',
    title: 'Edit column',
    subActions: async () => {
      const settings = await getColumnSettings({
        ruleSessionId: sessionId,
        headerId: header.Id,
      });

      return conditionalArray<IHeaderSubAction>(
        { name: 'Add Column After', action: () => onAddAfter({ header }) },
        { name: 'Add Column Before', action: () => onAddBefore({ header }) },
        [{ name: 'Move to Right', action: () => onMoveColumnToRight({ header }) }, settings.CanMoveRight],
        [{ name: 'Move to Left', action: () => onMoveColumnToLeft({ header }) }, settings.CanMoveLeft],
      );
    },
  });

  if (canDelete) {
    /* TODO: alex: need to change "tt-result-*" class name as it is not "result" action*/
    actions.push({
      iconClassName: 'tt-result-remove',
      title: 'Remove column',
      action: () => onDeleteClick?.({ header }),
    });
  }

  const handleAnchorClick = () => {
    !isDebugMode &&
      onAnchorClick?.({
        header: header,
        target: anchorRef.current as HTMLButtonElement,
      });
  };

  const handleVerbClick = () => {
    !isDebugMode &&
      onVerbClick?.({
        header: header,
        target: verbRef.current as HTMLButtonElement,
      });
  };

  useDidUpdateEffect(() => {
    // we cannot pick verb if anchor is not selected
    if (header.AnchorInfo && verbText === '[Pick Verb]') {
      handleVerbClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [header.AnchorInfo]);

  return (
    <PhraseColumn isHeader data-column-name={getPhraseColumnName(header)}>
      <Cell className={getClassName(className)}>
        {headerIssues && <ValidationIcon validationIssues={headerIssues} />}
        {header.EvaluateWithNot && (
          <PhraseItem onClick={handleAnchorClick} disabled={isDebugMode}>
            NOT
          </PhraseItem>
        )}
        <PhraseItem ref={anchorRef} onClick={handleAnchorClick} disabled={isDebugMode}>
          {anchorText}
        </PhraseItem>
        <PhraseItem ref={verbRef} onClick={handleVerbClick} disabled={isDebugMode}>
          {verbText}
        </PhraseItem>
        {!isDebugMode && <HeaderActions actions={actions} />}
        {header.ResolvedAnchorValue && (
          <ResolvedAnchorValue
            className="tt-header-phrase__resolved-data"
            resolvedText={header.ResolvedAnchorValue}
          />
        )}
      </Cell>
    </PhraseColumn>
  );
}
